import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sessionService } from '../../services/session.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import SubmissionSteps, { SubmissionStep } from '../../components/filing/SubmissionSteps';

type LetterVersion = {
  id: string;
  text: string;
  generatedAt?: string | { _seconds: number; _nanoseconds?: number };
};

type AppealSessionResponse = {
  sessionId: string;
  appealLetter?: { text?: string; generatedAt?: any } | null;
  appealLetterVersions?: LetterVersion[];
  generationCount?: number;
  generationLimit?: number;
};

const formatTimestamp = (value?: any) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object' && typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000).toISOString();
  }
  return String(value);
};

const PORTAL_URL = 'https://secure.toronto.ca/webapps/parking/';

const submissionSteps: SubmissionStep[] = [
  {
    id: 'portal',
    number: 1,
    title: 'Open the City portal',
    description: 'Use the official City of Toronto portal to dispute a parking violation.',
    note: 'Use only toronto.ca pages to avoid scams.',
    cta: {
      label: 'Open Toronto Portal',
      url: PORTAL_URL,
    },
  },
  {
    id: 'lookup',
    number: 2,
    title: 'Enter lookup info',
    description:
      'Fill in Violation Notice Number and Licence Plate. Choose the lookup option “Violation Notice Number and Licence Plate”.',
    image: {
      src: '/filing-guide/dispute1.png',
      alt: 'Lookup form for violation notice number and licence plate',
    },
  },
  {
    id: 'view',
    number: 3,
    title: 'View your notice(s)',
    description: 'Click “View Parking Violation Notice(s)”.',
    image: {
      src: '/filing-guide/dispute1.png',
      alt: 'View parking violation notice button',
    },
  },
  {
    id: 'dispute',
    number: 4,
    title: 'Start dispute',
    description: 'Click “Dispute this violation notice” in the table row action.',
    image: {
      src: '/filing-guide/dispute1.png',
      alt: 'Dispute this violation notice action in table row',
    },
  },
  {
    id: 'screening',
    number: 5,
    title: 'Request Online Screening',
    description: 'Click “Request Online Screening” (not hearing).',
    image: {
      src: '/filing-guide/dispute2.png',
      alt: 'Request Online Screening option',
    },
  },
  {
    id: 'paste',
    number: 6,
    title: 'Paste the letter',
    description:
      'Paste into “Dispute Description”. The textarea shows a 3000 characters remaining counter.',
    image: {
      src: '/filing-guide/dispute3.png',
      alt: 'Dispute Description textarea in the online form',
    },
  },
];

const TicketFilingGuidePage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const guideRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<AppealSessionResponse | null>(null);
  const [versions, setVersions] = useState<LetterVersion[]>([]);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const [supplementalInfo, setSupplementalInfo] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [usage, setUsage] = useState<{
    limitCents: number;
    remainingCents: number;
    monthTotalCents: number;
    totalsByType: Record<string, number>;
    recentEvents: Array<{ id: string; type: string; costCentsEstimated: number; createdAt?: any }>;
  } | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    const loadSession = async () => {
      setLoading(true);
      try {
        const response = await sessionService.getAppealSession(sessionId);
        if (!active) return;
        setSession(response);
        const storedVersions = Array.isArray(response?.appealLetterVersions)
          ? response.appealLetterVersions
          : [];
        const currentLetter = response?.appealLetter?.text;
        const fallbackVersions = currentLetter
          ? [
              {
                id: 'current',
                text: currentLetter,
                generatedAt: response?.appealLetter?.generatedAt,
              },
            ]
          : [];
        const mergedVersions = storedVersions.length > 0 ? storedVersions : fallbackVersions;
        setVersions(mergedVersions);
        if (mergedVersions.length > 0) {
          setActiveVersionId(mergedVersions[mergedVersions.length - 1].id);
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to load appeal session.');
      } finally {
        if (active) setLoading(false);
      }
    };
    loadSession();
    return () => {
      active = false;
    };
  }, [sessionId]);

  useEffect(() => {
    let active = true;
    const loadUsage = async () => {
      try {
        const response = await sessionService.getUsageMe();
        if (active) setUsage(response);
      } catch (error) {
        // Ignore usage errors to avoid blocking the page.
      }
    };
    loadUsage();
    return () => {
      active = false;
    };
  }, []);

  const maxVersions = 8;
  const generationsUsed = Math.max(session?.generationCount ?? 0, versions.length);
  const limitReached = generationsUsed >= maxVersions;
  const usageLimitReached = usage ? usage.remainingCents <= 0 : false;

  const activeVersion = useMemo(
    () => versions.find((item) => item.id === activeVersionId) || versions[versions.length - 1] || null,
    [activeVersionId, versions]
  );

  const handleRegenerate = async () => {
    if (!sessionId || regenerating || limitReached) return;
    setRegenerating(true);
    setRegenerateError(null);
    try {
      const supplementalBlock = supplementalInfo.trim()
        ? `\n\nUSER SUPPLEMENTAL INFO:\n${supplementalInfo.trim()}`
        : '';
      const currentLetter = activeVersion?.text || session?.appealLetter?.text || '';
      const response = await sessionService.generateAppealLetter(sessionId, {
        language: i18n.language,
        currentLetter: `${currentLetter}${supplementalBlock}`.trim() || undefined,
        termsAccepted: true,
      });
      const newVersion = {
        id: `v_${Date.now()}`,
        text: response.text || '',
        generatedAt: new Date().toISOString(),
      };
      setVersions((prev) => [...prev, newVersion].slice(-maxVersions));
      setActiveVersionId(newVersion.id);
      setSupplementalInfo('');
      setSession((prev) =>
        prev
          ? {
              ...prev,
              generationCount: response.count,
              generationLimit: response.limit,
            }
          : prev
      );
    } catch (error: any) {
      setRegenerateError(error.message || 'Failed to regenerate letter.');
    } finally {
      setRegenerating(false);
    }
  };

  const getActiveLetterText = () => activeVersion?.text || '';

  const normalizeForTextarea = (value: string) => {
    const trimmed = value.trim().replace(/\r\n/g, '\n');
    const lines = trimmed.split('\n').map((line) => {
      const withoutHeading = line.replace(/^\s*#{1,6}\s+/, '');
      const withoutBullet = withoutHeading.replace(/^\s*([-*+]|\\d+\\.)\\s+/, '');
      return withoutBullet;
    });
    return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  };

  const handleCopyRaw = async () => {
    const text = getActiveLetterText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Unable to copy.');
    }
  };

  const handleCopyForTextarea = async () => {
    const text = getActiveLetterText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(normalizeForTextarea(text));
      toast.success('Copied for Dispute Description');
    } catch (error) {
      toast.error('Unable to copy.');
    }
  };

  const handleContinue = () => {
    if (guideRef.current) {
      guideRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (sessionId) {
      navigate(`/ticket/${sessionId}/filing`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-12">
        <div className="max-w-5xl mx-auto px-4">
          <LoadingSpinner size="lg" text="Loading filing guide..." />
        </div>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-12">
        <div className="max-w-5xl mx-auto px-4 text-gray-700">
          Session not found.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10 text-slate-900">
      <div className="max-w-7xl mx-auto px-4 space-y-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2">
              <span className="rounded-full border border-[#EAD9B5] bg-[#FFF7E5] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#B88933]">
                Premium Guide
              </span>
              <span className="text-xs text-slate-400">Case progress</span>
            </div>
            <h1 className="font-display text-4xl font-bold text-slate-900">Filing guide</h1>
            <p className="text-slate-600">
              Follow the submission steps and keep refining your appeal if needed.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#C6A87C] to-[#A7854F] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(166,133,79,0.6)] transition-transform hover:-translate-y-0.5"
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div ref={guideRef} className="lg:sticky lg:top-20 lg:self-start">
            <SubmissionSteps steps={submissionSteps} portalUrl={PORTAL_URL} />
          </div>

          <div className="space-y-4">
            {usageLimitReached && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                You’ve reached your $5 usage limit. Please upgrade or contact support.
              </div>
            )}

            <div className="rounded-2xl border border-[#E7D9B8] bg-white/90 p-6 shadow-[0_20px_45px_-30px_rgba(15,23,42,0.35)] space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Your appeal letter</h2>
                  <p className="text-sm text-slate-600">
                    {generationsUsed}/{maxVersions} regenerations used
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-lg border border-[#E7D9B8] px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 disabled:opacity-60"
                    onClick={handleCopyRaw}
                    disabled={!activeVersion?.text}
                  >
                    Copy letter
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-[#C6A87C] px-3 py-1.5 text-xs font-semibold text-white shadow-[0_10px_30px_-12px_rgba(166,133,79,0.6)] disabled:opacity-60"
                    onClick={handleCopyForTextarea}
                    disabled={!activeVersion?.text}
                  >
                    Copy for Dispute Description
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-[#E7D9B8] bg-[#FFFCF5] p-3 text-xs text-slate-700">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                  <span>✅ Violation Notice Number ready</span>
                  <span>✅ Licence Plate ready</span>
                  <span>✅ Ready to paste into “Dispute Description”</span>
                </div>
              </div>

              {!activeVersion?.text && (
                <div className="text-xs text-slate-500">Generate your letter first.</div>
              )}

              <div className="flex flex-wrap gap-2">
                {versions.map((version, index) => (
                  <button
                    key={version.id}
                    type="button"
                    onClick={() => setActiveVersionId(version.id)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      activeVersionId === version.id
                        ? 'bg-[#C6A87C] text-white'
                        : 'bg-[#F4EBDC] text-slate-700 hover:bg-[#EAD9B5]'
                    }`}
                  >
                    Version {index + 1}
                  </button>
                ))}
              </div>

              <div className="rounded-xl border border-[#E7D9B8] bg-[#FFFCF5] p-4 text-sm text-slate-800 whitespace-pre-wrap min-h-[220px]">
                {activeVersion?.text || 'No letter generated yet.'}
              </div>
              <div className="text-xs text-slate-500">
                Last updated: {formatTimestamp(activeVersion?.generatedAt) || 'Generated just now'}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-900">Add more details (optional)</label>
                <p className="text-xs text-slate-600">
                  If something important is missing, add it here and regenerate.
                </p>
                <textarea
                  className="w-full rounded-xl border-[#E7D9B8] bg-white p-3 text-sm text-slate-700 shadow-sm focus:border-[#C6A87C] focus:ring-[#C6A87C]"
                  rows={4}
                  value={supplementalInfo}
                  onChange={(event) => setSupplementalInfo(event.target.value)}
                  maxLength={500}
                />
              </div>

              {regenerateError && <div className="text-sm text-red-600">{regenerateError}</div>}
              <button
                type="button"
                className="w-full rounded-xl bg-gradient-to-r from-[#C6A87C] to-[#A7854F] px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(166,133,79,0.6)] disabled:opacity-60"
                onClick={handleRegenerate}
                disabled={regenerating || limitReached || usageLimitReached}
              >
                {regenerating ? 'Regenerating...' : 'Regenerate letter'}
              </button>
              {limitReached && (
                <div className="text-xs text-slate-500">
                  You’ve reached the maximum number of regenerations.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketFilingGuidePage;
